import React from 'react';
import { User } from 'lucide-react';

/**
 * UserAvatar - Displays user profile picture or random default avatar
 * 
 * Features:
 * - Circular avatar display
 * - Custom uploaded avatar support
 * - Random default avatar from DiceBear (unique per user)
 * - Various sizes: xs, sm, md, lg, xl
 * - Online status indicator (optional)
 * - Click handler for profile editing
 * 
 * Usage:
 * <UserAvatar 
 *   user={{ id: "123", firstName: "John", lastName: "Doe", avatarUrl: "/api/avatars/123.jpg" }}
 *   size="md"
 *   showStatus={true}
 *   isOnline={true}
 *   onClick={() => openProfile()}
 * />
 */

const sizeClasses = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
  '2xl': 'w-20 h-20 text-xl'
};

const statusSizeClasses = {
  xs: 'w-2 h-2 -bottom-0 -right-0',
  sm: 'w-2.5 h-2.5 -bottom-0 -right-0',
  md: 'w-3 h-3 -bottom-0.5 -right-0.5',
  lg: 'w-3.5 h-3.5 -bottom-0.5 -right-0.5',
  xl: 'w-4 h-4 -bottom-1 -right-1',
  '2xl': 'w-5 h-5 -bottom-1 -right-1'
};

// DiceBear avatar styles - fun, colorful options
const AVATAR_STYLES = [
  'adventurer',      // Illustrated adventure characters
  'adventurer-neutral', // Gender-neutral adventure characters
  'avataaars',       // Cartoon-style avatars
  'big-ears',        // Characters with big ears
  'big-smile',       // Happy smiling characters
  'bottts',          // Robot avatars
  'croodles',        // Hand-drawn style
  'fun-emoji',       // Fun emoji faces
  'lorelei',         // Artistic portraits
  'micah',           // Simple illustrated people
  'miniavs',         // Minimalist avatars
  'open-peeps',      // Illustrated people
  'personas',        // Character personas
  'pixel-art',       // Retro pixel art
  'thumbs',          // Thumb characters
];

// Generate a consistent style index based on user identifier
const getStyleIndex = (seed) => {
  if (!seed) return 0;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash) % AVATAR_STYLES.length;
};

// Generate default avatar URL using DiceBear
const getDefaultAvatarUrl = (user) => {
  // Create a unique seed from user data
  const seed = user?.id || user?.email || `${user?.firstName || ''}${user?.lastName || ''}` || 'default';
  
  // Pick a consistent avatar style for this user
  const styleIndex = getStyleIndex(seed);
  const style = AVATAR_STYLES[styleIndex];
  
  // DiceBear API URL with customizations
  // Using the seed ensures the same user always gets the same avatar
  return `https://api.dicebear.com/7.x/${style}/svg?seed=${encodeURIComponent(seed)}&backgroundColor=f97316,f59e0b,84cc16,10b981,06b6d4,3b82f6,8b5cf6,ec4899&backgroundType=gradientLinear`;
};

const UserAvatar = ({ 
  user,
  size = 'md',
  showStatus = false,
  isOnline = false,
  onClick,
  className = '',
  editable = false,
  showDefaultAvatar = true // Set to false to show initials instead of random avatar
}) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [defaultAvatarError, setDefaultAvatarError] = React.useState(false);

  // Get initials from user name (final fallback)
  const getInitials = () => {
    if (!user) return '?';
    const first = user.firstName?.[0] || user.name?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  // Get custom avatar URL
  const getCustomAvatarUrl = () => {
    if (!user?.avatarUrl) return null;
    return user.avatarUrl;
  };

  const customAvatarUrl = getCustomAvatarUrl();
  const defaultAvatarUrl = showDefaultAvatar ? getDefaultAvatarUrl(user) : null;
  
  // Show custom avatar if available and no error, otherwise show default
  const showCustomAvatar = customAvatarUrl && !imageError;
  const showDefaultAvatarImg = !showCustomAvatar && showDefaultAvatar && !defaultAvatarError;

  // Reset error state when user changes
  React.useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
    setDefaultAvatarError(false);
  }, [user?.avatarUrl, user?.id]);

  const containerClasses = `
    relative inline-flex items-center justify-center 
    ${sizeClasses[size]} 
    rounded-full overflow-hidden flex-shrink-0
    ${onClick ? 'cursor-pointer' : ''}
    ${editable ? 'group' : ''}
    ${className}
  `.trim();

  return (
    <div 
      className={containerClasses}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Custom Avatar Image (uploaded by user) */}
      {showCustomAvatar && (
        <img
          src={customAvatarUrl}
          alt={`${user?.firstName || 'User'}'s avatar`}
          className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}

      {/* Default Avatar Image (random from DiceBear) */}
      {showDefaultAvatarImg && !showCustomAvatar && (
        <img
          src={defaultAvatarUrl}
          alt={`${user?.firstName || 'User'}'s default avatar`}
          className="w-full h-full object-cover"
          onError={() => setDefaultAvatarError(true)}
        />
      )}

      {/* Final Fallback: Initials with gradient background */}
      {!showCustomAvatar && !showDefaultAvatarImg && (
        <div className="w-full h-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
          <span className="text-white font-semibold">
            {getInitials()}
          </span>
        </div>
      )}

      {/* Editable overlay */}
      {editable && (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <User className="w-1/3 h-1/3 text-white" />
        </div>
      )}

      {/* Online status indicator */}
      {showStatus && (
        <span 
          className={`
            absolute ${statusSizeClasses[size]} 
            rounded-full border-2 border-white
            ${isOnline ? 'bg-green-500' : 'bg-gray-400'}
          `}
        />
      )}
    </div>
  );
};

/**
 * UserAvatarStack - Display multiple avatars in a stacked view
 * Useful for showing course participants, team members, etc.
 */
export const UserAvatarStack = ({ 
  users = [], 
  max = 5, 
  size = 'sm',
  showOverflow = true 
}) => {
  const displayUsers = users.slice(0, max);
  const overflowCount = users.length - max;

  return (
    <div className="flex items-center -space-x-2">
      {displayUsers.map((user, index) => (
        <div 
          key={user.id || index} 
          className="relative"
          style={{ zIndex: displayUsers.length - index }}
        >
          <UserAvatar 
            user={user} 
            size={size}
            className="ring-2 ring-white"
          />
        </div>
      ))}
      {showOverflow && overflowCount > 0 && (
        <div 
          className={`
            ${sizeClasses[size]} 
            rounded-full bg-gray-200 border-2 border-white 
            flex items-center justify-center
          `}
          style={{ zIndex: 0 }}
        >
          <span className="text-gray-600 font-medium">+{overflowCount}</span>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
